import React from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider, App as AntdApp } from "antd";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import store from "./store/store";
import AppLayout from "./components/AppLayout";
import PortalLayout from "./components/PortalLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import { RoleRoute, ModuleRoute } from "./components/AccessRoute";
import { ROLES, MODULES } from "./hooks/useAccess";
import Login from "./Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import ClientsList from "./pages/Clients/ClientsList";
import ClientFormPage from "./pages/Clients/ClientFormPage";
import ClientUsage from "./pages/Clients/ClientUsage";
import PackagesList from "./pages/Packages/PackagesList";
import PackageFormPage from "./pages/Packages/PackageFormPage";
import SubscriptionList from "./pages/Subscription/SubscriptionList";
import SubscriptionDetail from "./pages/Subscription/SubscriptionDetail";
import PaymentsList from "./pages/Payments/PaymentsList";
import PaymentFormPage from "./pages/Payments/PaymentFormPage";
import AgentsList from "./pages/Agents/AgentsList";
import AgentDetail from "./pages/Agents/AgentDetail";
import LlmKeysList from "./pages/LlmKeys/LlmKeysList";
import LlmModelsList from "./pages/LlmModels/LlmModelsList";
import ApiKeysList from "./pages/ApiKeys/ApiKeysList";
import TokenValuesList from "./pages/TokenValues/TokenValuesList";
import CostMarginReport from "./pages/Reports/CostMarginReport";
import FeedbacksList from "./pages/Feedbacks/FeedbacksList";
import UsersList from "./pages/Users/UsersList";
import BackupsPage from "./pages/Backups/BackupsPage";
import RedisMonitor from "./pages/Redis/RedisMonitor";
import PluginsPage from "./pages/Plugins/PluginsPage";
import ChangePassword from "./pages/Account/ChangePassword";
import Profile from "./pages/Account/Profile";
import PortalAgents from "./pages/Portal/PortalAgents";
import PortalAgentDetail from "./pages/Portal/PortalAgentDetail";
import PortalUsage from "./pages/Portal/PortalUsage";
import PortalSubscription from "./pages/Portal/PortalSubscription";
import PortalInvoices from "./pages/Portal/PortalInvoices";
// InvoicesList removed - invoices now shown inline in Payments page
import "./App.css";
import { MessageBridge } from "./components/MessageBridge ";
import LogsPage from "./pages/Logs System/LogsPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";

const queryClient = new QueryClient();

const BACKOFFICE_ROLES = [ROLES.OWNER, ROLES.ADMIN, ROLES.DEVELOPER];

function AppContent() {
  const { themeConfig } = useTheme();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ConfigProvider theme={themeConfig} modal={{ zIndex: 2000 }}>
          <AntdApp>
            <MessageBridge />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Reachable by every role, including accounts still on a temporary
                  password — this is the screen that clears that state. */}
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />

              {/* Client portal */}
              <Route
                element={
                  <ProtectedRoute>
                    <RoleRoute allow={[ROLES.CLIENT]}>
                      <PortalLayout />
                    </RoleRoute>
                  </ProtectedRoute>
                }
              >
                <Route path="portal" element={<PortalAgents />} />
                <Route path="portal/profile" element={<Profile />} />
                <Route path="portal/agents/:id" element={<PortalAgentDetail />} />
                <Route path="portal/usage" element={<PortalUsage />} />
                <Route path="portal/subscription" element={<PortalSubscription />} />
                <Route path="portal/invoices" element={<PortalInvoices />} />
              </Route>

              {/* Back-office. Each screen additionally requires its module grant;
                  Owner/Admin pass every ModuleRoute unconditionally. */}
              <Route
                element={
                  <ProtectedRoute>
                    <RoleRoute allow={BACKOFFICE_ROLES}>
                      <AppLayout />
                    </RoleRoute>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />

                {/* Own profile and security settings — no module grant, every role has one. */}
                <Route path="profile" element={<Profile />} />

                <Route element={<ModuleRoute module={MODULES.CLIENTS} />}>
                  <Route path="clients" element={<ClientsList />} />
                  <Route path="clients/new" element={<ClientFormPage />} />
                  <Route path="clients/:id/edit" element={<ClientFormPage />} />
                  <Route path="clients/:id/usage" element={<ClientUsage />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.PACKAGES} />}>
                  <Route path="packages" element={<PackagesList />} />
                  <Route path="packages/new" element={<PackageFormPage />} />
                  <Route path="packages/:id/edit" element={<PackageFormPage />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.LOGS} />}>
                  <Route path="logs" element={<LogsPage />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.TOKEN_VALUES} />}>
                  <Route path="tokenvalues" element={<TokenValuesList />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.REPORTS} />}>
                  <Route path="reports/cost-margin" element={<CostMarginReport />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.SUBSCRIPTIONS} />}>
                  <Route path="subscription" element={<SubscriptionList />} />
                  <Route path="subscription/:id" element={<SubscriptionDetail />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.PAYMENTS} />}>
                  <Route path="payments" element={<PaymentsList />} />
                  <Route path="payments/new" element={<PaymentFormPage />} />
                  <Route path="payments/:id/edit" element={<PaymentFormPage />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.AGENTS} />}>
                  <Route path="agents" element={<AgentsList />} />
                  <Route path="agents/:id" element={<AgentDetail />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.LLM_MODELS} />}>
                  <Route path="llm-models" element={<LlmModelsList />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.LLM_KEYS} />}>
                  <Route path="llm-keys" element={<LlmKeysList />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.API_KEYS} />}>
                  <Route path="apikeys" element={<ApiKeysList />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.FEEDBACKS} />}>
                  <Route path="feedbacks" element={<FeedbacksList />} />
                </Route>

                <Route element={<ModuleRoute module={MODULES.USERS} />}>
                  <Route path="users" element={<UsersList />} />
                </Route>

                {/* Backup/restore rewrites the whole database, so it is gated on the
                    Owner/Admin role rather than on a module grant — matching the API. */}
                <Route element={<RoleRoute allow={[ROLES.OWNER, ROLES.ADMIN]} />}>
                  <Route path="backups" element={<BackupsPage />} />
                  {/* Plugins carries the SMTP credentials, so it shares the backup gate. */}
                  <Route path="plugins" element={<PluginsPage />} />
                  {/* Redis exposes platform-wide operational figures — same gate again. */}
                  <Route path="redis" element={<RedisMonitor />} />
                </Route>
              </Route>
            </Routes>
          </AntdApp>
        </ConfigProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
